import { AssistantProvider } from '@wianco/portal-assistant';
import '@wianco/portal-assistant/styles.css';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { config } from '@/config/env';
import { useAuthStore } from '@/features/auth/store';
import { useLanguageStore } from '@/features/language/store';
import { useThemeStore } from '@/features/theme/store';
import { defaultNavItems } from '@/features/navigation/config';
import { useAssistantAccess } from './useAssistantAccess';

const getAuthToken = () => useAuthStore.getState().ensureValidToken(30);

/** The only handoff to Assistant: session lifecycle and explicit route context. */
export function PortalAssistantProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const authenticated = useAuthStore((state) => state.isAuthenticated);
  const { canViewAssistant, canUseAssistantAvatars } = useAssistantAccess();
  const userId = useAuthStore((state) => state.user?.sub ?? state.user?.email);
  const customerId = useAuthStore((state) => state.hubspotCustomerId);
  const language = useLanguageStore((state) => state.language);
  const theme = useThemeStore((state) => state.theme);
  const area = defaultNavItems.find(
    (item) =>
      item.path &&
      (location.pathname === item.path || location.pathname.startsWith(`${item.path}/`))
  );
  const settings = config().assistant;
  return (
    <AssistantProvider
      sessionKey={authenticated ? (userId ?? 'authenticated') : null}
      enabled={canViewAssistant}
      allowAvatars={canUseAssistantAvatars}
      apiBaseUrl={settings?.apiBaseUrl ?? 'https://app.elunos.ai'}
      publicToken={settings?.publicToken ?? 'wgt_dee8b51208903454119e1c8ec08cb50b'}
      getAuthToken={getAuthToken}
      context={{
        pathname: location.pathname,
        service: area?.id,
        title: area?.label,
        language,
        customerId,
      }}
      theme={theme}
    >
      {children}
    </AssistantProvider>
  );
}
